import { PACKAGE_FRACTIONS } from './config';
import { round2, round6 } from './math';
import type { NutrientKey, NutrientValues } from './types';

export type PackageFraction = 'entero' | 'medio' | 'cuarto';

const FRACTIONS: Record<PackageFraction, number> = PACKAGE_FRACTIONS;

/**
 * Regla de tres sobre los valores por 100 g (SPEC 6.B).
 *
 * Multiplica ANTES de dividir: `valor * gramos / 100` da 0,99 para 3 kcal/100 g
 * y 33 g, mientras que `valor * (gramos / 100)` da 0,9899999999999999.
 *
 * `null` sigue siendo `null`: un nutriente desconocido no se convierte en 0 al
 * escalarlo (SPEC 4.1).
 */
export function scaleNutrients(per100g: NutrientValues, grams: number): NutrientValues {
  if (!(grams >= 0)) throw new Error('Los gramos no pueden ser negativos');

  const out: NutrientValues = {};
  for (const [key, value] of Object.entries(per100g) as [
    NutrientKey,
    number | null | undefined,
  ][]) {
    out[key] = value == null ? null : round6((value * grams) / 100);
  }
  return out;
}

/**
 * Entero / Medio / Cuarto. Necesita el peso total del envase; si no se conoce
 * hay que pedirlo, no adivinarlo (SPEC 6.B).
 */
export function gramsFromFraction(
  packageGrams: number | null | undefined,
  fraction: PackageFraction,
): number {
  if (packageGrams == null || !(packageGrams > 0)) {
    throw new Error('Hace falta el peso del envase para usar Entero/Medio/Cuarto');
  }
  return round2(packageGrams * FRACTIONS[fraction]);
}

/**
 * Suma valores ya escalados.
 *
 * Un `null` no aporta pero tampoco borra lo que si se conoce: si al menos un
 * alimento trae el dato, el total es la suma de los conocidos. Si ninguno lo
 * trae, el total es `null` — no 0, porque "no lo se" y "cero" son cosas
 * distintas y de la segunda saldria un deficit inventado.
 */
export function sumNutrients(values: readonly NutrientValues[]): NutrientValues {
  const sums = new Map<NutrientKey, number>();
  const seen = new Set<NutrientKey>();

  for (const item of values) {
    for (const [key, value] of Object.entries(item) as [NutrientKey, number | null | undefined][]) {
      seen.add(key);
      if (value != null) sums.set(key, (sums.get(key) ?? 0) + value);
    }
  }

  const out: NutrientValues = {};
  for (const key of seen) {
    const sum = sums.get(key);
    out[key] = sum == null ? null : round6(sum);
  }
  return out;
}

/** Gramos a partir de una porcion domestica ("1 huevo" = 60 g). */
export function gramsFromHousehold(portionGrams: number, count: number): number {
  if (!(portionGrams > 0)) throw new Error('La porción debe pesar más de 0 g');
  if (!(count >= 0)) throw new Error('La cantidad no puede ser negativa');
  return round2(portionGrams * count);
}
